import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  Search,
  Command,
  Plus,
  CheckSquare,
  Home,
  LayoutDashboard,
  Eye,
  Settings,
  Users,
  Tag,
  AlertCircle,
  Circle,
  ArrowRight,
  Calendar,
  Clock,
  Link2,
  Sparkles
} from "lucide-react";
import { Input } from "../ui/input";
import { useWorkspace } from "../../contexts/workspace-context";
import { cn } from "../../lib/utils";

interface CommandItem {
  id: string;
  title: string;
  action: () => void;
  category: string;
  icon?: React.ReactNode;
  keywords?: string[];
}

interface SlashCommand {
  command: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export function CommandBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { currentWorkspace, workspaces, setCurrentWorkspace } = useWorkspace();

  // Slash commands configuration
  const slashCommands: SlashCommand[] = useMemo(() => [
    {
      command: "/task",
      title: "Create Task",
      description: "Create a new task in the current workspace",
      icon: <Plus className="h-4 w-4" />,
    },
    {
      command: "/go",
      title: "Go to...",
      description: "Navigate to a page or resource",
      icon: <ArrowRight className="h-4 w-4" />,
    },
    {
      command: "/status",
      title: "Change Status",
      description: "Update task status (TODO, In Progress, Done...)",
      icon: <Circle className="h-4 w-4" />,
    },
    {
      command: "/priority",
      title: "Set Priority",
      description: "Set task priority level",
      icon: <AlertCircle className="h-4 w-4" />,
    },
    {
      command: "/assign",
      title: "Assign Task",
      description: "Assign task to a team member",
      icon: <Users className="h-4 w-4" />,
    },
    {
      command: "/label",
      title: "Add Label",
      description: "Add or remove labels from tasks",
      icon: <Tag className="h-4 w-4" />,
    },
    {
      command: "/due",
      title: "Set Due Date",
      description: "Set or change the due date",
      icon: <Calendar className="h-4 w-4" />,
    },
    {
      command: "/schedule",
      title: "Schedule Task",
      description: "Schedule when to work on a task",
      icon: <Clock className="h-4 w-4" />,
    },
    {
      command: "/workspace",
      title: "Switch Workspace",
      description: "Switch to another workspace",
      icon: <LayoutDashboard className="h-4 w-4" />,
    },
    {
      command: "/integrations",
      title: "Integrations",
      description: "Manage connected apps and services",
      icon: <Link2 className="h-4 w-4" />,
    },
    {
      command: "/settings",
      title: "Settings",
      description: "Open workspace or app settings",
      icon: <Settings className="h-4 w-4" />,
    },
  ], []);

  // Regular commands
  const commands: CommandItem[] = useMemo(() => [
    {
      id: "my-work",
      title: "Go to My Work",
      action: () => navigate("/my-work"),
      category: "Navigation",
      icon: <Home className="h-4 w-4" />,
      keywords: ["home", "dashboard", "my", "work"],
    },
    {
      id: "digest",
      title: "Go to Digest",
      action: () => navigate("/digest"),
      category: "Navigation",
      icon: <Sparkles className="h-4 w-4" />,
      keywords: ["summary", "ai", "overview"],
    },
    {
      id: "tasks",
      title: "Go to Tasks",
      action: () => navigate("/tasks"),
      category: "Navigation",
      icon: <CheckSquare className="h-4 w-4" />,
      keywords: ["issues", "todos"],
    },
    {
      id: "views",
      title: "Go to Views",
      action: () => navigate("/views"),
      category: "Navigation",
      icon: <Eye className="h-4 w-4" />,
      keywords: ["board", "kanban"],
    },
    {
      id: "integrations",
      title: "Go to Integrations",
      action: () => navigate("/integrations"),
      category: "Navigation",
      icon: <Link2 className="h-4 w-4" />,
      keywords: ["github", "slack", "connect"],
    },
    // Workspace switching
    ...workspaces.filter(w => w.id !== currentWorkspace?.id).map(workspace => ({
      id: `switch-workspace-${workspace.id}`,
      title: `Switch to ${workspace.name}`,
      action: () => {
        setCurrentWorkspace(workspace);
        navigate("/my-work");
      },
      category: "Workspaces",
      icon: <LayoutDashboard className="h-4 w-4" />,
      keywords: ["workspace", "team", "switch", workspace.name.toLowerCase()],
    })),
  ], [navigate, workspaces, currentWorkspace, setCurrentWorkspace]);

  // Check if search starts with /
  const isSlashCommand = search.startsWith("/");
  const searchTerm = isSlashCommand ? search.slice(1).toLowerCase() : search.toLowerCase();

  // Filter slash commands
  const filteredSlashCommands = useMemo(() => {
    if (!isSlashCommand) return [];
    return slashCommands.filter(cmd =>
      cmd.command.slice(1).toLowerCase().includes(searchTerm) ||
      cmd.title.toLowerCase().includes(searchTerm)
    );
  }, [isSlashCommand, searchTerm, slashCommands]);

  // Filter regular commands
  const filteredCommands = useMemo(() => {
    if (isSlashCommand) return [];
    return commands.filter((cmd) =>
      cmd.title.toLowerCase().includes(search.toLowerCase()) ||
      cmd.keywords?.some(k => k.includes(search.toLowerCase()))
    );
  }, [commands, search, isSlashCommand]);

  // Combined filtered items for keyboard navigation
  const allFilteredItems = isSlashCommand ? filteredSlashCommands : filteredCommands;

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((open) => !open);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Handle keyboard navigation within the palette
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allFilteredItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (allFilteredItems.length > 0) {
        const selected = allFilteredItems[selectedIndex];
        if (isSlashCommand) {
          handleSlashCommandSelect(selected as SlashCommand);
        } else {
          handleSelect(selected as CommandItem);
        }
      }
    }
  }, [allFilteredItems, selectedIndex, isSlashCommand]);

  const handleSelect = (command: CommandItem) => {
    command.action();
    setIsOpen(false);
    setSearch("");
  };

  const handleSlashCommandSelect = (slashCmd: SlashCommand) => {
    switch (slashCmd.command) {
      case "/task":
        navigate("/tasks?create=true");
        break;
      case "/integrations":
        navigate("/integrations");
        break;
      case "/settings":
      case "/workspace":
        navigate("/settings");
        break;
      case "/go":
        navigate("/my-work");
        break;
      case "/status":
      case "/priority":
      case "/assign":
      case "/label":
      case "/due":
      case "/schedule":
        navigate("/tasks");
        break;
      default:
        break;
    }
    setIsOpen(false);
    setSearch("");
  };

  return (
    <>
      {/* Command trigger button (always visible at bottom) */}
      <div className="fixed bottom-6 left-0 right-0 md:left-[220px] z-40 flex justify-center px-4 group">
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            "w-full max-w-3xl flex items-center gap-3 px-5 py-3 backdrop-blur-md rounded-xl transition-all duration-500 ease-out",
            "opacity-30 hover:opacity-100 focus:opacity-100",
            "bg-gradient-to-r from-transparent via-transparent to-transparent",
            "hover:bg-gradient-to-r hover:from-card/90 hover:via-card/95 hover:to-card/90",
            "border border-transparent hover:border-primary/20 focus:border-primary/30",
            "shadow-none hover:shadow-[0_0_30px_-5px_rgba(var(--primary-rgb),0.15)]",
            "text-muted-foreground/70 hover:text-foreground",
            "group/btn"
          )}
        >
          <Search className="h-4 w-4 transition-transform duration-300 group-hover/btn:scale-110 group-hover/btn:text-primary" />
          <span className="flex-1 text-left font-light tracking-wide text-sm">Ask anything or type a command...</span>
          <div className="flex items-center gap-2">
            <kbd className={cn(
              "pointer-events-none inline-flex h-6 select-none items-center gap-1 rounded-md px-2",
              "bg-gradient-to-b from-sidebar/80 to-sidebar/60 border border-border/50",
              "font-mono text-xs text-muted-foreground/80",
              "transition-all duration-300 group-hover/btn:border-primary/30 group-hover/btn:text-primary/80"
            )}>
              <Command className="h-3 w-3" />K
            </kbd>
          </div>
        </button>
      </div>

      {/* Command palette overlay */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 animate-in fade-in duration-200"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-4xl z-50 px-4 animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className={cn(
              "bg-gradient-to-b from-card to-card/95 rounded-2xl overflow-hidden",
              "border border-border/50 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)]",
              "ring-1 ring-primary/5"
            )}>
              {/* Search input */}
              <div className="flex items-center border-b border-border/50 px-5 py-1 bg-gradient-to-r from-transparent via-primary/[0.02] to-transparent">
                <Search className="h-4 w-4 text-primary/60" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a command or search... (try /task, /status, /priority)"
                  className="border-0 focus-visible:ring-0 shadow-none text-base font-light tracking-wide placeholder:text-muted-foreground/50"
                  autoFocus
                />
              </div>

              {/* Slash commands hint */}
              {isSlashCommand && (
                <div className="px-5 py-2.5 border-b border-border/30 bg-gradient-to-r from-primary/[0.03] via-primary/[0.05] to-primary/[0.03]">
                  <span className="text-xs font-medium tracking-wider uppercase text-primary/60">
                    Slash commands — Quick actions
                  </span>
                </div>
              )}

              {/* Results */}
              <div className="max-h-96 overflow-y-auto p-3">
                {/* Slash Commands Results */}
                {isSlashCommand && (
                  <>
                    {filteredSlashCommands.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground/60 font-light">
                        No matching commands found.
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredSlashCommands.map((cmd, index) => (
                          <button
                            key={cmd.command}
                            onClick={() => handleSlashCommandSelect(cmd)}
                            className={cn(
                              "w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left",
                              "transition-all duration-200 ease-out",
                              selectedIndex === index
                                ? "bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-l-2 border-primary shadow-sm"
                                : "hover:bg-sidebar/50 border-l-2 border-transparent"
                            )}
                          >
                            <div className={cn(
                              "p-2 rounded-lg transition-colors duration-200",
                              selectedIndex === index ? "bg-primary/10 text-primary" : "bg-sidebar text-muted-foreground"
                            )}>
                              {cmd.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3">
                                <span className={cn(
                                  "font-mono text-xs px-2 py-0.5 rounded-md transition-colors duration-200",
                                  selectedIndex === index ? "bg-primary/20 text-primary" : "bg-sidebar text-primary/70"
                                )}>{cmd.command}</span>
                                <span className="text-foreground font-medium tracking-tight">{cmd.title}</span>
                              </div>
                              <div className="text-xs text-muted-foreground/70 mt-0.5 font-light">
                                {cmd.description}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Regular Commands Results */}
                {!isSlashCommand && (
                  <>
                    {filteredCommands.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground/60 font-light">
                        No results found.
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredCommands.map((command, index) => (
                          <button
                            key={command.id}
                            onClick={() => handleSelect(command)}
                            className={cn(
                              "w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left",
                              "transition-all duration-200 ease-out",
                              selectedIndex === index
                                ? "bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-l-2 border-primary shadow-sm"
                                : "hover:bg-sidebar/50 border-l-2 border-transparent"
                            )}
                          >
                            {command.icon && (
                              <div className={cn(
                                "p-2 rounded-lg transition-colors duration-200",
                                selectedIndex === index ? "bg-primary/10 text-primary" : "bg-sidebar text-muted-foreground"
                              )}>
                                {command.icon}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-foreground font-medium tracking-tight">
                                {command.title}
                              </div>
                              <div className="text-xs text-muted-foreground/70 mt-0.5 font-light uppercase tracking-wider">
                                {command.category}
                              </div>
                            </div>
                            <ArrowRight className={cn(
                              "h-4 w-4 transition-all duration-200",
                              selectedIndex === index ? "opacity-100 text-primary translate-x-0" : "opacity-0 -translate-x-2"
                            )} />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
